import React, { useEffect } from 'react'

import { TopFilters as TopFiltersWrapper } from '@app/components'
import { LntSelect, LntRangeDatePicker } from '@app/components/base'

import { FILTERS_PLACEHOLDERS } from '@app/constants/filters'
import { FILTERS_PARAMS_KEYS } from '@app/api/constants'
import { toDayjs } from '@app/utils/filters/adapters'

import { INTERNAL_TASKS_KEY, LOCATION_KEY, MONITORINGS_TYPES_KEY } from '@app/constants/queryKeys'
import { SIZE, dateFormat } from '@app/constants'

import { getTypes } from '@app/api/routes/monitorings'
import { getClusters } from '@app/api/routes/locationСlusters'
import { getSalesOrganization } from '@app/api/routes/locationSalesOrganization'
import { getExecutors } from '@app/api/routes/executors'
import { getFilterValue } from '@app/utils/filters'

import { useFiltersSearch } from '@app/hooks'
import { getFilterFromEntities, getFilterFromExecutors } from '@app/utils/entitiesAdapters'

import { getDayOfCurrentWeek } from '@app/utils/date'

import { commonSelectProps, executorOptions, statusesOptions, taskTypesOptions } from './configs'

import type { TopFilterProps } from './types'

const defaultDeadline = {
  [FILTERS_PARAMS_KEYS.deadlineFrom]: getDayOfCurrentWeek(1).format(dateFormat.YYYY_MM_DD),
  [FILTERS_PARAMS_KEYS.deadlineTo]: getDayOfCurrentWeek(7).format(dateFormat.YYYY_MM_DD)
}

export const TopFilters: React.FC<TopFilterProps> = ({ onSearch, initialFilters }) => {
  const {
    filters,
    setFilters,
    canResetFilters,
    handleClearFilters,
    handleSearch,
    handleSelectFilterChange,
    handleCreatedRangeDateChange
  } = useFiltersSearch({ initialFilters, onSearch })

  const resetDefaultPeriod = (): void => {
    setFilters(prev => ({
      ...prev,
      ...defaultDeadline
    }))
  }

  const setDefaultPeriod = (): void => {
    const hasQueryData = !!filters.deadlineFrom && !!filters.deadlineTo

    if (hasQueryData) return

    resetDefaultPeriod()

    onSearch({
      ...filters,
      ...defaultDeadline
    })
  }

  const handleClear = (): void => {
    handleClearFilters()
    resetDefaultPeriod()
  }

  useEffect(() => {
    setDefaultPeriod()
  }, [])

  return (
    <TopFiltersWrapper
      canReset={ canResetFilters }
      onClear={ handleClear }
      onSearch={ handleSearch }
    >
      <LntSelect
        showArrow
        strictValue
        size={ SIZE.middle }
        value={ filters.executor }
        options={ executorOptions }
        onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.executor, value) }
        placeholder={ FILTERS_PLACEHOLDERS.executor }
      />

      <LntSelect
        { ...commonSelectProps }
        value={ filters.taskTypes }
        options={ taskTypesOptions }
        onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.taskTypes, value) }
        placeholder={ FILTERS_PLACEHOLDERS.taskTypes }
      />

      <LntRangeDatePicker
        size={ SIZE.middle }
        value={ [
          getFilterValue(filters, FILTERS_PARAMS_KEYS.createdAtFrom, toDayjs),
          getFilterValue(filters, FILTERS_PARAMS_KEYS.createdAtTo, toDayjs)
        ] }
        format={ dateFormat.DD_MM_YYYY }
        onChange={ value =>
          handleCreatedRangeDateChange(value, [FILTERS_PARAMS_KEYS.createdAtFrom, FILTERS_PARAMS_KEYS.createdAtTo])
        }
        placeholder={ [FILTERS_PLACEHOLDERS.createdAtFrom, FILTERS_PLACEHOLDERS.createdAtTo] }
      />

      <LntRangeDatePicker
        allowClear={ false }
        size={ SIZE.middle }
        value={ [
          getFilterValue(filters, FILTERS_PARAMS_KEYS.deadlineFrom, toDayjs),
          getFilterValue(filters, FILTERS_PARAMS_KEYS.deadlineTo, toDayjs)
        ] }
        format={ dateFormat.DD_MM_YYYY }
        onChange={ value =>
          handleCreatedRangeDateChange(value, [FILTERS_PARAMS_KEYS.deadlineFrom, FILTERS_PARAMS_KEYS.deadlineTo])
        }
        placeholder={ [FILTERS_PLACEHOLDERS.deadlineFrom, FILTERS_PLACEHOLDERS.deadlineTo] }
      />

      <LntSelect
        { ...commonSelectProps }
        value={ filters.statuses }
        options={ statusesOptions }
        onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.statuses, value) }
        placeholder={ FILTERS_PLACEHOLDERS.status }
      />

      <LntSelect
        { ...commonSelectProps }
        value={ filters.executorUserIds }
        onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.executorUserIds, value) }
        placeholder={ FILTERS_PLACEHOLDERS.executorFilter }
        querySelect={ {
          queryKey: [INTERNAL_TASKS_KEY.internalTasksExecutorItems],
          queryFn: getExecutors,
          select: getFilterFromExecutors
        } }
      />

      <LntSelect
        { ...commonSelectProps }
        value={ filters.salesOrganizationIds }
        onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.salesOrganizationIds, value) }
        placeholder={ FILTERS_PLACEHOLDERS.salesOrganizationFilter }
        querySelect={ {
          queryKey: [LOCATION_KEY.locationSalesOrganizationsItems],
          queryFn: getSalesOrganization,
          select: getFilterFromEntities
        } }
      />

      <LntSelect
        { ...commonSelectProps }
        value={ filters.monitoringTypeIds }
        onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.monitoringTypeIds, value) }
        placeholder={ FILTERS_PLACEHOLDERS.monitoringsTypeFilter }
        querySelect={ {
          queryKey: [MONITORINGS_TYPES_KEY.typesItems],
          queryFn: getTypes,
          select: getFilterFromEntities
        } }
      />

      <LntSelect
        { ...commonSelectProps }
        value={ filters.clusterIds }
        onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.clusterIds, value) }
        placeholder={ FILTERS_PLACEHOLDERS.clustersFilter }
        querySelect={ {
          queryKey: [LOCATION_KEY.locationClustersItems],
          queryFn: getClusters,
          select: getFilterFromEntities
        } }
      />
    </TopFiltersWrapper>
  )
}
